import { QueryClient, QueryCache, MutationCache, QueryFunction } from "@tanstack/react-query";
import { AuthService } from "./auth";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/** 401 を受け取ったらログイン画面に飛ばす（既にログイン画面なら何もしない） */
function handle401IfNeeded(error: unknown) {
  if (typeof window === "undefined") return;
  const msg = error instanceof Error ? error.message : String(error);
  if (!msg.startsWith("401")) return;
  AuthService.getInstance().resetAuthStatus();
  const { pathname, search } = window.location;
  if (pathname.startsWith("/login")) return;
  // ログイン後に元の場所へ戻れるよう referrer を付与（login側で使う場合）
  const next = encodeURIComponent(pathname + search);
  window.location.href = `/login?next=${next}`;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handle401IfNeeded,
  }),
  mutationCache: new MutationCache({
    onError: handle401IfNeeded,
  }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
